import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { SUBSCRIPTION_TIERS, UPGRADE_PROMPTS } from "../data/content";
import { Ornament } from "./Ornament";
import api from "../lib/api";

export const UpgradeModal = ({ open, onClose, reason }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/stats/founding").then((r) => setStats(r.data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const copy = UPGRADE_PROMPTS[reason] || UPGRADE_PROMPTS.default;
  const foundingOpen = stats?.available !== false;
  const remaining = stats?.remaining;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      style={{ background: "rgba(17,29,51,0.82)" }}
      data-testid="upgrade-modal"
      onClick={onClose}
    >
      <div
        className="max-w-4xl w-full p-6 sm:p-12 relative my-8"
        style={{ background: "#111D33", borderRadius: 4, border: "1px solid #B8973A" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-[#E8D5A3] hover:text-white transition-colors" aria-label="Close" data-testid="upgrade-modal-close">
          <X size={20} />
        </button>
        <div className="text-center mb-8">
          <p className="label-ui text-[11px] text-[#B8973A] mb-4">An invitation</p>
          <h2 className="text-3xl sm:text-4xl text-[#FAF7F2]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{copy.headline}</h2>
          <Ornament className="my-6" color="#B8973A" />
          <p className="italic text-[#E8D5A3] max-w-xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", lineHeight: 1.55 }}>
            {copy.body}
          </p>
          {foundingOpen && remaining != null && (
            <p className="label-ui text-[10px] text-[#E8D5A3] mt-5" data-testid="upgrade-founding-counter">
              ✦ {remaining} founding member places remaining out of 500
            </p>
          )}
          {!foundingOpen && (
            <p className="label-ui text-[10px] text-[#8C8278] mt-5">Founding membership is now closed. Standard pricing applies.</p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.values(SUBSCRIPTION_TIERS).map((t, i) => {
            const variant = foundingOpen ? t.founding : t.standard;
            return (
              <div key={t.sku} className="p-6 sm:p-8 relative bg-[#1B2A4A]" style={{ borderRadius: 4, border: "1px solid #B8973A" }} data-testid={`upgrade-option-${t.sku}`}>
                {variant.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 label-ui text-[10px] whitespace-nowrap" style={{ background: "#B8973A", color: "#FAF7F2", borderRadius: 2 }}>
                    {variant.badge}
                  </div>
                )}
                <p className="chapter-roman">{i === 0 ? "I" : "II"}</p>
                <h3 className="text-2xl text-[#FAF7F2] mt-3 mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t.name}</h3>
                <p className="italic text-[#E8D5A3] mb-5" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>{t.headline}</p>
                <div className="text-4xl text-[#FAF7F2] mb-1" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  £{variant.price}<span className="text-base text-[#8C8278]"> / year</span>
                </div>
                {foundingOpen && (
                  <p className="label-ui text-[10px] text-[#B8973A] mb-5">Founding rate · price stays the same while your membership is active</p>
                )}
                <ul className="space-y-2 mb-7 mt-4">
                  {t.features.map((f, j) => (
                    <li key={j} className="text-sm text-[#E8D5A3] flex items-start gap-3" style={{ fontFamily: "Georgia, serif", lineHeight: 1.6 }}>
                      <span className="text-[#B8973A] mt-0.5">✦</span><span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => { onClose?.(); navigate(`/subscribe?plan=${t.sku}`); }}
                  className="btn-gold w-full"
                  data-testid={`upgrade-choose-${t.sku}`}
                >
                  Choose {t.name}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-[#8C8278] mt-6 italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "0.95rem" }}>
          Cancel anytime. Your Story Week responses remain in your account whether or not you become a Story Keeper.
        </p>
      </div>
    </div>
  );
};

export default UpgradeModal;
